import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, BarChart3, CheckCircle2, CircleAlert, Network, ShieldCheck, Sparkles, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MARKETING_CTA } from './marketingConfig';
import { usePageMeta } from '../../hooks/usePageMeta';

function PageShell({
  title,
  description,
  path,
  kicker,
  children,
}: {
  title: string;
  description: string;
  path: string;
  kicker: string;
  children: ReactNode;
}) {
  usePageMeta({ title, description, path });

  return (
    <div className="mx-auto max-w-6xl px-4 pt-14 sm:px-6 lg:px-8">
      <p className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-3 py-1 text-xs text-brand">
        <Sparkles className="h-3.5 w-3.5" /> {kicker}
      </p>
      <h1 className="mt-5 text-4xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-4 max-w-3xl text-gray-300">{description}</p>
      <div className="mt-8 space-y-5 pb-10">{children}</div>
    </div>
  );
}

function SectionCard({ title, description, children }: { title: string; description: string; children?: ReactNode }) {
  return (
    <section className="rounded-2xl border border-surface-4 bg-surface-1 p-6">
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="mt-2 text-sm text-gray-400">{description}</p>
      {children}
    </section>
  );
}

export function MarketingFeaturesPage() {
  const reduced = useReducedMotion();

  return (
    <PageShell
      title="Features"
      description="Interactive modules, validation lifecycle visibility, and institutional control surfaces built for serious sports intelligence operations."
      path="/features"
      kicker="Pulse capability matrix"
    >
      <motion.section
        initial={reduced ? undefined : { opacity: 0, y: 12 }}
        whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        className="grid gap-4 lg:grid-cols-2"
      >
        <SectionCard
          title="Capability Matrix"
          description="Cross-module intelligence, trust, portfolio, and analytics capabilities by workflow stage."
        >
          <div className="mt-4 grid gap-2 text-sm">
            {[
              ['Ingest', 'Market + context feeds', 'Realtime'],
              ['Analyze', 'Multi-agent consensus engine', 'Realtime'],
              ['Validate', 'Evidence + uncertainty checks', 'Gate-controlled'],
              ['Deliver', 'Recommendation dashboard', 'User-facing'],
              ['Audit', 'Lineage and lifecycle records', 'Persistent'],
            ].map(([stage, capability, mode]) => (
              <div key={stage} className="grid grid-cols-12 gap-2 rounded border border-surface-4 bg-surface-2 px-3 py-2">
                <span className="col-span-3 font-medium text-gray-200">{stage}</span>
                <span className="col-span-6 text-gray-300">{capability}</span>
                <span className="col-span-3 text-right text-brand">{mode}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title="Product Modules"
          description="Sport-specific engines operate inside a shared Pulse command center governance layer."
        >
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {['DiamondEdge', 'GridironEdge', 'CourtEdge', 'IceEdge', 'PitchEdge', 'Future engines'].map((item) => (
              <article key={item} className="rounded-xl border border-surface-4 bg-surface-2 p-4 transition hover:-translate-y-0.5 hover:border-brand/40">
                <p className="font-medium">{item}</p>
                <p className="mt-1 text-xs text-gray-400">Unified data contracts • Validation policy inheritance</p>
              </article>
            ))}
          </div>
        </SectionCard>
      </motion.section>

      <SectionCard
        title="Expandable feature categories"
        description="Workflow previews and lifecycle examples designed for rapid process comprehension."
      >
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            {
              title: 'Intelligence',
              points: ['Agent consensus overlays', 'Market movement telemetry', 'Cross-slate grouping'],
            },
            {
              title: 'Trust',
              points: ['Evidence cards', 'Blocked recommendation traces', 'Uncertainty disclosures'],
            },
            {
              title: 'Portfolio',
              points: ['Exposure heatmaps', 'Risk warnings', 'Lifecycle performance review'],
            },
          ].map((group) => (
            <details key={group.title} className="rounded-xl border border-surface-4 bg-surface-2 p-4">
              <summary className="cursor-pointer text-sm font-semibold">{group.title}</summary>
              <ul className="mt-3 space-y-1 text-xs text-gray-300">
                {group.points.map((point) => (
                  <li key={point}>• {point}</li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </SectionCard>

      <div className="flex flex-wrap gap-3">
        <Link to={MARKETING_CTA.requestAccess.to} className="btn-primary inline-flex items-center gap-2">
          {MARKETING_CTA.requestAccess.label} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/how-it-works" className="btn-ghost border border-surface-4">
          See Recommendation Lifecycle
        </Link>
      </div>
    </PageShell>
  );
}

export function MarketingHowItWorksPage() {
  const reduced = useReducedMotion();

  return (
    <PageShell
      title="How It Works"
      description="A cinematic, transparent intelligence pipeline from ingestion to audited recommendation delivery."
      path="/how-it-works"
      kicker="Ingestion → analysis → validation → delivery"
    >
      <motion.section
        initial={reduced ? undefined : { opacity: 0, y: 12 }}
        whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        className="overflow-x-auto pb-2"
        data-testid="lifecycle-pipeline"
      >
        <ol aria-label="Recommendation pipeline stages" className="flex min-w-max items-stretch gap-0">
          {[
            ['01', 'Ingest', 'Market, lineup, injury, and context signals normalized', 'done'],
            ['02', 'Analyze', 'Specialized agents generate hypotheses in parallel', 'done'],
            ['03', 'Validate', 'Consistency, risk, and confidence calibration checks', 'gate'],
            ['04', 'Deliver', 'Explainable recommendation and portfolio framing', 'active'],
            ['05', 'Audit', 'Lifecycle records for review and accountability', 'pending'],
          ].map(([index, title, text, state], i, arr) => (
            <div key={title} className="flex items-stretch">
              <article
                className={[
                  'w-44 rounded-xl border bg-surface-1 p-4',
                  state === 'gate'
                    ? 'border-sky-400/40 bg-sky-500/5'
                    : state === 'done'
                      ? 'border-emerald-400/25'
                      : state === 'active'
                        ? 'border-brand/30'
                        : 'border-surface-4',
                ].join(' ')}
              >
                <p
                  className={`text-xs font-mono ${
                    state === 'done'
                      ? 'text-emerald-400'
                      : state === 'gate'
                        ? 'text-sky-300'
                        : state === 'active'
                          ? 'text-brand'
                          : 'text-gray-600'
                  }`}
                >
                  {index}
                </p>
                <h2 className="mt-2 text-sm font-semibold">{title}</h2>
                <p className="mt-1.5 text-xs leading-relaxed text-gray-400">{text}</p>
              </article>
              {i < arr.length - 1 && (
                <div aria-hidden className="flex items-center px-1.5 pb-6">
                  <div
                    className={[
                      'h-px w-4',
                      i < 2 ? 'bg-emerald-400/35' : i === 2 ? 'bg-sky-400/30' : 'bg-white/10',
                    ].join(' ')}
                  />
                  <span
                    className={`text-xs ${i < 2 ? 'text-emerald-400/50' : i === 2 ? 'text-sky-400/50' : 'text-gray-700'}`}
                  >
                    ›
                  </span>
                </div>
              )}
            </div>
          ))}
        </ol>
      </motion.section>

      <SectionCard
        title="Agent orchestration diagram"
        description="Signal ingestion fan-out, confidence contribution, and consensus routing into validation gates."
      >
        <div className="mt-4 space-y-2.5">
          {[
            { agent: 'Statistical', delta: '+0.22', pct: 88, tone: 'emerald', negative: false, warning: false },
            { agent: 'Market movement', delta: '+0.18', pct: 72, tone: 'sky', negative: false, warning: false },
            { agent: 'Injury + news', delta: '−0.09', pct: 36, tone: 'amber', negative: true, warning: false },
            { agent: 'Portfolio risk', delta: '⚠ medium', pct: 60, tone: 'orange', negative: false, warning: true },
          ].map((item) => (
            <div key={item.agent} className="rounded-lg border border-surface-4 bg-surface-2 p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-200">{item.agent} agent</p>
                <span
                  className={`text-xs tabular-nums font-mono ${
                    item.warning ? 'text-amber-300' : item.negative ? 'text-red-300' : 'text-emerald-300'
                  }`}
                >
                  {item.delta}
                </span>
              </div>
              <div aria-hidden className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                <div
                  className={`h-full rounded-full ${
                    item.tone === 'emerald'
                      ? 'bg-emerald-400/60'
                      : item.tone === 'sky'
                        ? 'bg-sky-400/60'
                        : item.tone === 'amber'
                          ? 'bg-amber-400/60'
                          : 'bg-orange-400/60'
                  }`}
                  style={{ width: `${item.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-lg border border-sky-400/30 bg-sky-400/5 p-4" role="status">
          <p className="mb-1.5 text-xs font-semibold text-sky-200">Consensus core → Validation gate</p>
          <p className="text-xs text-gray-300">
            3 of 4 agents converged above release threshold. Portfolio risk agent flagged exposure warning — validation gate initiated before delivery.
          </p>
        </div>
      </SectionCard>

      <SectionCard
        title="Recommendation lifecycle explorer"
        description="Follow recommendation status from draft to released or blocked with explicit reason codes."
      >
        <div className="mt-4 space-y-2 text-sm">
          {[
            ['Draft', 'Awaiting confidence calibration'],
            ['Validation', 'Line movement sanity check running'],
            ['Released', 'Reasoning, evidence, and risk context attached'],
            ['Blocked', 'Risk concentration threshold exceeded'],
          ].map(([status, detail]) => (
            <div key={status} className="flex items-center justify-between rounded border border-surface-4 bg-surface-2 px-3 py-2">
              <span className="font-medium text-gray-200">{status}</span>
              <span className="text-xs text-gray-300">{detail}</span>
            </div>
          ))}
        </div>
      </SectionCard>
    </PageShell>
  );
}

export function MarketingPricingPage() {
  return (
    <PageShell
      title="Pricing"
      description="Premium comparison for operators who value process rigor, transparency, and scalable intelligence operations."
      path="/pricing"
      kicker="Tiered intelligence access"
    >
      <SectionCard
        title="Plan comparison matrix"
        description="Choose the operating mode that fits your workflow and depth requirements."
      >
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="rounded-l-lg border border-surface-4 bg-surface-2 px-3 py-2 text-left">Capability</th>
                <th className="border border-surface-4 bg-surface-2 px-3 py-2">Free</th>
                <th className="border border-brand/30 bg-brand/10 px-3 py-2">Pro</th>
                <th className="rounded-r-lg border border-surface-4 bg-surface-2 px-3 py-2">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Pulse command center', 'Core', 'Full', 'Full + SSO'],
                ['Validation engine', 'Basic', 'Advanced', 'Policy controls'],
                ['Portfolio analytics', 'Starter', 'Lifecycle', 'Org-level'],
                ['Support model', 'Community', 'Priority', 'Dedicated'],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="border border-surface-4 px-3 py-2 text-gray-200">{row[0]}</td>
                  <td className="border border-surface-4 px-3 py-2 text-center text-gray-300">{row[1]}</td>
                  <td className="border border-brand/30 px-3 py-2 text-center text-brand">{row[2]}</td>
                  <td className="border border-surface-4 px-3 py-2 text-center text-gray-300">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          {
            plan: 'Free',
            bestFor: 'Evaluation and onboarding',
            icon: CheckCircle2,
            cta: MARKETING_CTA.requestAccess.label,
            to: MARKETING_CTA.requestAccess.to,
          },
          {
            plan: 'Pro',
            bestFor: 'Serious operators and model-driven workflows',
            icon: BarChart3,
            cta: 'Compare Plans',
            to: '/pricing',
          },
          {
            plan: 'Enterprise',
            bestFor: 'Teams requiring governance and roadmap alignment',
            icon: ShieldCheck,
            cta: 'Contact Sales',
            to: '/about',
          },
        ].map((card) => (
          <article key={card.plan} className="rounded-2xl border border-surface-4 bg-surface-1 p-5">
            <card.icon className="h-4 w-4 text-brand" />
            <h2 className="mt-3 text-lg font-semibold">{card.plan}</h2>
            <p className="mt-2 text-sm text-gray-300">Best for: {card.bestFor}</p>
            <Link to={card.to} className="btn-primary mt-4 inline-flex">
              {card.cta}
            </Link>
          </article>
        ))}
      </div>

      <SectionCard
        title="ROI and process value framing"
        description="Navyrx Sports is designed to improve decision quality through repeatable process, not guarantee outcomes."
      >
        <p className="mt-4 text-sm text-gray-300">
          Better evidence discipline, validation consistency, and risk-aware exposure management compound over
          long horizons. Responsible use remains mandatory across all tiers.
        </p>
      </SectionCard>
    </PageShell>
  );
}

export function MarketingAboutPage() {
  return (
    <PageShell
      title="About Navyrx Sports"
      description="Why Navyrx exists: to replace hype-driven picks culture with explainable, institutional-grade sports intelligence discipline."
      path="/about"
      kicker="Mission, philosophy, and roadmap"
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard
          title="Mission and vision"
          description="Build the operating system for transparent, disciplined sports decision intelligence at scale."
        >
          <ul className="mt-4 space-y-2 text-sm text-gray-300">
            <li>• Structured intelligence over impulsive decision-making</li>
            <li>• Explainable recommendations over black-box picks</li>
            <li>• Portfolio discipline over emotional wagering behavior</li>
          </ul>
        </SectionCard>

        <SectionCard
          title="Operational philosophy"
          description="Institutional process standards applied to sports intelligence workflows."
        >
          <ul className="mt-4 space-y-2 text-sm text-gray-300">
            <li className="inline-flex gap-2"><Workflow className="mt-0.5 h-4 w-4 text-brand" /> Process before outcomes</li>
            <li className="inline-flex gap-2"><Network className="mt-0.5 h-4 w-4 text-brand" /> Multi-agent consensus before release</li>
            <li className="inline-flex gap-2"><CircleAlert className="mt-0.5 h-4 w-4 text-brand" /> Uncertainty disclosure as default behavior</li>
          </ul>
        </SectionCard>
      </div>

      <SectionCard
        title="Transparency manifesto"
        description="Trust is earned with evidence, validation logs, and visible risk controls."
      >
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            'Every recommendation has reasoning for and against.',
            'Blocked recommendations are retained in an auditable trail.',
            'Exposure warnings and confidence context are always disclosed.',
          ].map((item) => (
            <div key={item} className="rounded-xl border border-surface-4 bg-surface-2 p-4 text-sm text-gray-300">
              {item}
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Future ecosystem roadmap"
        description="Pulse command center expansion across additional sports engines with shared governance."
      >
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {['DiamondEdge (MLB)', 'GridironEdge (NFL)', 'CourtEdge (NBA/WNBA)', 'IceEdge (NHL)', 'PitchEdge (Soccer)', 'Future engines'].map(
            (item) => (
              <div key={item} className="rounded-lg border border-surface-4 bg-surface-2 px-3 py-2 text-sm text-gray-300">
                {item}
              </div>
            ),
          )}
        </div>
      </SectionCard>
    </PageShell>
  );
}
